import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, ChevronUp, HelpCircle, Calendar, FileText, School, Users } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useScrollToTop } from '../hooks/useScrollToTop';

const FAQ: React.FC = () => {
  useScrollToTop();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>('general');

  const faqCategories = [
    {
      id: 'general',
      title: 'General Information',
      icon: <HelpCircle className="h-6 w-6 text-[#7B4AE2]" />,
      questions: [
        {
          q: 'What is FYJC?',
          a: 'FYJC stands for First Year Junior College, which corresponds to Class 11 in the Indian education system. It marks the commencement of higher secondary education in Maharashtra.'
        },
        {
          q: 'Who is eligible to apply for FYJC admissions?',
          a: 'Students who have successfully passed the Class 10 (SSC) examination or its equivalent from a recognized board are eligible to apply for FYJC admissions.'
        }
      ]
    },
    {
      id: 'dates',
      title: 'Important Dates',
      icon: <Calendar className="h-6 w-6 text-[#18CDCA]" />,
      questions: [
        {
          q: 'What are the key dates for FYJC admission process in 2025?',
          a: `• Application Start Date: May 19, 2025
• Part 1 Application Deadline: June 15, 2025 (4:00 PM)
• Part 2 Application Period: June 5 – June 16, 2025
• First Merit List Release: June 18, 2025
• Seat Allotment Period: June 21 – June 25, 2025
• Seat Confirmation Deadline: June 29, 2025`
        }
      ]
    },
    {
      id: 'process',
      title: 'Admission Process',
      icon: <School className="h-6 w-6 text-[#FFD166]" />,
      questions: [
        {
          q: 'How is the admission process structured?',
          a: `The admission process comprises two main parts:
• Part 1: Registration and submission of personal and academic details
• Part 2: Selection of preferred colleges and streams`
        },
        {
          q: 'What documents are required for the application?',
          a: `Applicants need to provide:
• Class 10 Marksheet
• School Leaving Certificate (SLC)
• Caste Certificate (if applicable)
• Minority Certificate (if applicable)
• Sports Certificate (if applicable)
• EWS Certificate (if applicable)
• Disability Certificate (if applicable)`
        },
        {
          q: 'Are there any application fees?',
          a: `Yes, the application fees are:
• Mumbai Region: ₹250
• Other Regions: ₹150`
        }
      ]
    },
    {
      id: 'quotas',
      title: 'Reservations & Quotas',
      icon: <Users className="h-6 w-6 text-[#EF476F]" />,
      questions: [
        {
          q: 'What reservation policies are in place?',
          a: `The reservation criteria include:
• Scheduled Castes (SC): 13%
• Scheduled Tribes (ST): 7%
• Vimukta Jati (VJ-A): 3%
• Nomadic Tribes (NT-B): 2.5%
• Nomadic Tribes (NT-C): 3.5%
• Nomadic Tribes (NT-D): 2%
• Special Backward Class (SBC): 2%
• Other Backward Class (OBC): 19%
• Economically Weaker Section (EWS): 10%
• Open Category: 38%`
        },
        {
          q: 'What quotas are available?',
          a: `The following quotas are available:
• In-house Quota: 10%
• Management Quota: 5%
• Minority Quota: 50% (applicable only in minority institutions)`
        }
      ]
    },
    {
      id: 'application',
      title: 'Application Steps',
      icon: <FileText className="h-6 w-6 text-[#7B4AE2]" />,
      questions: [
        {
          q: 'How can I apply for FYJC admissions?',
          a: `Follow these steps:
1. Visit the official website: mahafyjcadmissions.in
2. Register: Create a new account by providing necessary details
3. Fill Part 1: Enter personal and academic information
4. Upload Documents: Submit scanned copies of required documents
5. Pay Fees: Complete the payment process
6. Fill Part 2: Select preferred colleges and streams
7. Submit Application: Review and submit the application form`
        }
      ]
    }
  ];

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => 
        q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* FAQ Header */}
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Find answers to common questions about FYJC admissions for 2025-26
            </p>
            
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions..."
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7B4AE2] focus:border-[#7B4AE2] transition duration-150 ease-in-out"
              />
            </div>
          </div>
          
          {/* FAQ Categories */}
          <div className="max-w-4xl mx-auto">
            {filteredCategories.map((category) => (
              <div key={category.id} className="mb-6">
                <button
                  onClick={() => setExpandedCategory(
                    expandedCategory === category.id ? null : category.id
                  )}
                  className="w-full flex items-center justify-between bg-white rounded-lg shadow-sm p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="mr-4">
                      {category.icon}
                    </div>
                    <h2 className="text-xl font-semibold">{category.title}</h2>
                  </div>
                  {expandedCategory === category.id ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                
                {expandedCategory === category.id && (
                  <div className="mt-2 space-y-2">
                    {category.questions.map((faq, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-lg shadow-sm p-6"
                      >
                        <h3 className="text-lg font-medium text-gray-900 mb-3">
                          {faq.q}
                        </h3>
                        <div className="text-gray-600 whitespace-pre-line">
                          {faq.a}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Additional Help */}
          <div className="max-w-4xl mx-auto mt-12">
            <div className="bg-[#F0E9FF] rounded-lg p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Still have questions?</h2>
              <p className="text-gray-600 mb-6">
                Can't find what you're looking for? Check our detailed help center or contact our support team.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link 
                  to="/help" 
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors"
                >
                  Visit Help Center
                </Link>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center justify-center px-6 py-3 border border-[#7B4AE2] text-[#7B4AE2] rounded-lg hover:bg-[#F0E9FF] transition-colors"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default FAQ;